import { useCallback, useEffect, useState } from 'react';
import { HOME } from '@shared/home';
import { dateKeyInZone, eventsInNextFiveDays } from '@shared/events';
import { api } from '@/lib/api';
import { hasWorkerApi } from '@/lib/hasWorkerApi';
import type { TownEvent } from '@shared/types';

function emptyBundle() {
  const today = dateKeyInZone(new Date(), HOME.timezone);
  return { today, events: [] as TownEvent[], nextFive: [] as TownEvent[] };
}

export function useEvents() {
  const seed = emptyBundle();
  const [today, setToday] = useState<string | null>(seed.today);
  const [events, setEvents] = useState<TownEvent[]>([]);
  const [nextFive, setNextFive] = useState<TownEvent[]>([]);
  const [loading, setLoading] = useState(() => hasWorkerApi());
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!hasWorkerApi()) {
      const fallback = emptyBundle();
      setToday(fallback.today);
      setEvents([]);
      setNextFive([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    api
      .get<{ today: string; events: TownEvent[]; nextFive: TownEvent[] }>('/events', 8000)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.events) ? res.events : [];
        setToday(res.today);
        setEvents(list);
        setNextFive(res.nextFive?.length ? res.nextFive : eventsInNextFiveDays(list, res.today));
        setError(false);
      })
      .catch(() => {
        if (cancelled) return;
        const fallback = emptyBundle();
        setToday(fallback.today);
        setEvents([]);
        setNextFive([]);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const applyTitleEnglish = useCallback((title: string, english: string) => {
    const patch = (list: TownEvent[]) =>
      list.map((event) => (event.title === title ? { ...event, titleEn: english } : event));
    setEvents(patch);
    setNextFive(patch);
  }, []);

  return { today, events, nextFive, loading, error, applyTitleEnglish };
}
